/**
 * Kiosk Customizer Page
 * Main page component for kiosk UI customization
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useKioskCustomizerStore } from '../stores/kiosk-customizer-store';
import { useDevicePreviewStore } from '../../core/stores/device-preview-store';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/core/components/ui/tabs';
import { FormFactorSelector } from '../../core/components/shared/FormFactorSelector';
import { SaveConfigButton } from '../../core/components/shared/SaveConfigButton';
import { ResetButtons } from '../../core/components/shared/ResetButtons';
import { PreviewScale } from '../../core/components/shared/PreviewScale';
import { ColorPicker } from '../../core/components/shared/ColorPicker';
import { Section } from '../../core/components/shared/Section';
import { IconUploadZone } from '../../core/components/shared/IconUploadZone';
import { LogoUploadZone } from '../../core/components/shared/LogoUploadZone';
import { LogoCropDialog } from '../../core/components/shared/LogoCropDialog';
import { IdleScreenTab } from './editor/IdleScreenTab';
import { OperatorSwitcher } from './OperatorSwitcher';
import { UnsavedChangesDialog } from '../../core/components/shared/UnsavedChangesDialog';
import { KIOSK_FORM_FACTORS } from '../constants/kiosk-form-factors';
import { Loader2, AlertCircle } from 'lucide-react';
import { KioskMockup4inch } from './preview/KioskMockup4inch';
import { KioskMockup5inch } from './preview/KioskMockup5inch';
import { validateLogoFile, createFilePreviewUrl } from '../../core/utils/file-helpers';
import { toast } from 'sonner';
import { Button } from '@/core/components/ui/button';

/**
 * Kiosk Customizer Page Component
 * Split layout: 40% editor (left) + 60% preview (right)
 */
export function KioskCustomizerPage() {
  const { operatorId: routeOperatorId } = useParams<{ operatorId?: string }>();
  const { selectedOperator } = useOperatorStore();
  const scale = useDevicePreviewStore((state) => state.scale);
  const setScale = useDevicePreviewStore((state) => state.setScale);

  const {
    config,
    currentOperatorId,
    currentOperatorName,
    isLoading,
    isSaving,
    isUploadingIcon,
    hasUnsavedChanges,
    isDefault,
    pendingMedia4Logo,
    pendingMedia5Logo,
    showCropDialog,
    cropDialogTarget,
    setCurrentOperator,
    loadConfig,
    updateThemeColor,
    uploadIcon,
    switchFormFactor,
    saveConfig,
    discardChanges,
    reset,
    setPendingLogo,
    clearPendingLogo,
    updateCropArea,
    openCropDialog,
    closeCropDialog,
  } = useKioskCustomizerStore();

  // Debug log for save button state
  console.log('[KioskCustomizerPage] State:', {
    currentOperatorId,
    currentOperatorName,
    hasUnsavedChanges,
    isDefault,
    isSaving,
    isLoading,
  });

  // Refs for auto-scaling
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [autoScale, setAutoScale] = useState(true);

  // State for unsaved changes dialog
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingOperatorSwitch, setPendingOperatorSwitch] = useState<{
    operatorId: string;
    operatorName: string;
  } | null>(null);

  // Operator auto-detection on mount
  useEffect(() => {
    // Priority 1: URL parameter (explicit)
    if (routeOperatorId) {
      const operatorName =
        selectedOperator?.operatorId.toString() === routeOperatorId
          ? selectedOperator.name
          : `Operator ${routeOperatorId}`;
      setCurrentOperator(routeOperatorId, operatorName);
      return;
    }

    // Priority 2: Selected operator from store
    if (selectedOperator?.operatorId) {
      setCurrentOperator(selectedOperator.operatorId.toString(), selectedOperator.name);
      return;
    }

    // No operator available - error state handled in render
  }, [routeOperatorId, selectedOperator, setCurrentOperator]);

  // Auto-scale mockup to fit container
  useEffect(() => {
    if (!autoScale || !previewContainerRef.current) return;

    const calculateScale = () => {
      const container = previewContainerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Get mockup dimensions based on form factor
      const mockupWidth = config.formFactor === '4inch' ? 480 : 420;
      const mockupHeight = config.formFactor === '4inch' ? 480 : 720;

      // Calculate scale to fit (with padding for better spacing)
      const paddingWidth = 80;
      const paddingHeight = 100;

      const scaleWidth = (containerWidth - paddingWidth) / mockupWidth;
      const scaleHeight = (containerHeight - paddingHeight) / mockupHeight;

      // Use the smaller scale to ensure it fits both dimensions
      let newScale = Math.min(scaleWidth, scaleHeight);

      // For 4" mockup, allow scaling up to fill space nicely
      // For 5" mockup, use 125% as default scale
      if (config.formFactor === '4inch') {
        newScale = Math.min(newScale, 1.5); // Max 150% for 4"
      } else {
        newScale = Math.min(newScale, 1.25); // Max 125% for 5"
      }

      // Ensure minimum scale of 0.2 (20%) for very small screens
      newScale = Math.max(0.2, newScale);

      setScale(newScale);
    };

    // Initial calculation with a small delay to ensure DOM is ready
    const timer = setTimeout(calculateScale, 100);

    // Recalculate on window resize
    const resizeObserver = new ResizeObserver(() => {
      calculateScale();
    });

    if (previewContainerRef.current) {
      resizeObserver.observe(previewContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [config.formFactor, autoScale, setScale]);

  // Operator change handler
  const handleOperatorChange = async (newOperatorId: string, newOperatorName: string) => {
    if (hasUnsavedChanges) {
      // Show confirmation dialog
      setShowUnsavedChangesDialog(true);
      setPendingOperatorSwitch({ operatorId: newOperatorId, operatorName: newOperatorName });
    } else {
      // Switch immediately
      await setCurrentOperator(newOperatorId, newOperatorName);
    }
  };

  // Unsaved changes dialog handlers
  const handleSaveAndSwitch = async () => {
    if (!currentOperatorId || !pendingOperatorSwitch) return;

    try {
      // Save current config
      await saveConfig(parseInt(currentOperatorId));

      // Switch to new operator
      await setCurrentOperator(pendingOperatorSwitch.operatorId, pendingOperatorSwitch.operatorName);

      // Close dialog
      setShowUnsavedChangesDialog(false);
      setPendingOperatorSwitch(null);

      toast.success('Configuration saved and operator switched');
    } catch (error) {
      console.error('Failed to save and switch:', error);
      toast.error('Failed to save configuration');
    }
  };

  const handleDiscardAndSwitch = async () => {
    if (!pendingOperatorSwitch) return;

    // Discard changes
    discardChanges();

    // Switch to new operator
    await setCurrentOperator(pendingOperatorSwitch.operatorId, pendingOperatorSwitch.operatorName);

    // Close dialog
    setShowUnsavedChangesDialog(false);
    setPendingOperatorSwitch(null);
  };

  const handleCancelSwitch = () => {
    setShowUnsavedChangesDialog(false);
    setPendingOperatorSwitch(null);
  };

  // Event handlers for logo upload (applies to both displays)
  const handleLogoSelect = (_target: 'media4' | 'media5', file: File) => {
    const validation = validateLogoFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const originalImageUrl = createFilePreviewUrl(file);

    // Apply to both media4 and media5
    setPendingLogo('media4', { file, originalImageUrl, previewUrl: originalImageUrl });
    setPendingLogo('media5', { file, originalImageUrl, previewUrl: originalImageUrl });

    // Don't auto-open crop dialog - user can click "Crop" button if they want to crop
    // This makes cropping optional rather than mandatory
  };

  const handleLogoCrop = () => {
    // Open crop dialog when user clicks the "Crop" button
    openCropDialog('media4');
  };

  const handleLogoRemove = () => {
    // Clear both logos
    clearPendingLogo('media4');
    clearPendingLogo('media5');
  };

  const handleCropConfirm = (croppedBlob: Blob, cropArea: any) => {
    if (cropDialogTarget) {
      // Apply crop to both media4 and media5
      updateCropArea('media4', croppedBlob, cropArea);
      updateCropArea('media5', croppedBlob, cropArea);
    }
    closeCropDialog();
  };

  const getCurrentCropImageUrl = () => {
    // Always return the original uncropped image for the crop dialog
    if (cropDialogTarget === 'media4' && pendingMedia4Logo) {
      return pendingMedia4Logo.originalImageUrl;
    }
    if (cropDialogTarget === 'media5' && pendingMedia5Logo) {
      return pendingMedia5Logo.originalImageUrl;
    }
    return '';
  };

  const getCurrentCropArea = () => {
    // Return the existing crop area if available (for re-cropping)
    if (cropDialogTarget === 'media4' && pendingMedia4Logo) {
      return pendingMedia4Logo.cropArea;
    }
    if (cropDialogTarget === 'media5' && pendingMedia5Logo) {
      return pendingMedia5Logo.cropArea;
    }
    return undefined;
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading kiosk configuration...</p>
        </div>
      </div>
    );
  }

  if (!currentOperatorId) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">No Operator Selected</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Please select an operator to customize kiosk settings.
            </p>
          </div>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => window.history.back()}>
              Go Back
            </Button>
            <Button onClick={() => (window.location.href = '/')}>
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const formFactorOptions = KIOSK_FORM_FACTORS.map((ff) => ({
    value: ff.id,
    label: ff.displayName.split(' ')[0],
    dimensions: `${ff.dimensions.width}x${ff.dimensions.height}`,
  }));

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border bg-background px-6 py-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Kiosk UI Customization</h1>
            <p className="text-sm text-muted-foreground">
              Customize the appearance of your EV charging kiosk screens
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <OperatorSwitcher
            currentOperatorId={currentOperatorId}
            currentOperatorName={currentOperatorName}
            onOperatorChange={handleOperatorChange}
            disabled={isLoading || isSaving}
          />
          <ResetButtons
            hasUnsavedChanges={hasUnsavedChanges}
            isDefault={isDefault}
            onResetToSaved={discardChanges}
            onResetToDefault={reset}
          />
          <SaveConfigButton
            onSave={() => {
              console.log('[KioskCustomizerPage] Save clicked', {
                currentOperatorId,
                isSaving,
                hasUnsavedChanges,
              });
              saveConfig(parseInt(currentOperatorId));
            }}
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        targetOperatorName={pendingOperatorSwitch?.operatorName || ''}
        onSave={handleSaveAndSwitch}
        onDiscard={handleDiscardAndSwitch}
        onCancel={handleCancelSwitch}
      />

      {/* Main content: Editor + Preview */}
      <div className="flex flex-1 overflow-hidden">
        {/* Editor Panel (40%) */}
        <div className="w-2/5 overflow-y-auto border-r border-border bg-background p-6">
          <div className="space-y-6">
            {/* Form Factor Selector */}
            <FormFactorSelector
              options={formFactorOptions}
              value={config.formFactor}
              onChange={(ff) => switchFormFactor(ff as '4inch' | '5inch')}
            />

            {/* Tabs for different editor sections */}
            <Tabs defaultValue="theme" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="theme">Theme</TabsTrigger>
                <TabsTrigger value="media">Media</TabsTrigger>
                <TabsTrigger value="layout">Layout</TabsTrigger>
              </TabsList>

              {/* Theme Tab */}
              <TabsContent value="theme" className="space-y-6">
                <Section title="Background Colors" description="Main screen colors">
                  <ColorPicker
                    label="Primary Background"
                    value={config.theme.colors.primaryBackground}
                    onChange={(color) => updateThemeColor('primaryBackground', color)}
                  />
                  <ColorPicker
                    label="Secondary Background"
                    value={config.theme.colors.secondaryBackground}
                    onChange={(color) => updateThemeColor('secondaryBackground', color)}
                    description="Used for cards and panels"
                  />
                </Section>

                <Section title="Text Colors" description="Text and label colors">
                  <ColorPicker
                    label="Primary Text"
                    value={config.theme.colors.primaryText}
                    onChange={(color) => updateThemeColor('primaryText', color)}
                  />
                  <ColorPicker
                    label="Secondary Text"
                    value={config.theme.colors.secondaryText}
                    onChange={(color) => updateThemeColor('secondaryText', color)}
                  />
                </Section>

                <Section title="Status Colors" description="Connector status indicators">
                  <ColorPicker
                    label="Available"
                    value={config.theme.colors.statusSuccess}
                    onChange={(color) => updateThemeColor('statusSuccess', color)}
                  />
                  <ColorPicker
                    label="Connected"
                    value={config.theme.colors.statusInfo}
                    onChange={(color) => updateThemeColor('statusInfo', color)}
                  />
                  <ColorPicker
                    label="Occupied"
                    value={config.theme.colors.statusWarning}
                    onChange={(color) => updateThemeColor('statusWarning', color)}
                  />
                  <ColorPicker
                    label="Unavailable"
                    value={config.theme.colors.statusError}
                    onChange={(color) => updateThemeColor('statusError', color)}
                  />
                </Section>
              </TabsContent>

              {/* Media Tab - Logo and Idle Screen */}
              <TabsContent value="media" className="space-y-6">
                <Section title="Logo" description="Logo for both 4-inch and 5-inch displays">
                  <LogoUploadZone
                    label="Kiosk Display Logo"
                    target="media4"
                    currentLogo={config.icons.media4Logo || config.icons.media5Logo}
                    pendingLogo={pendingMedia4Logo}
                    disabled={isSaving}
                    onFileSelect={(file) => handleLogoSelect('media4', file)}
                    onRemove={handleLogoRemove}
                    onCrop={handleLogoCrop}
                  />
                </Section>

                <IdleScreenTab />
              </TabsContent>

              {/* Layout Tab */}
              <TabsContent value="layout" className="space-y-6">
                <Section
                  title="Layout Settings"
                  description="Spacing and dimensions (advanced users)"
                >
                  <p className="text-sm text-muted-foreground">
                    Layout settings are automatically adjusted based on form factor selection.
                    Manual adjustments coming in future release.
                  </p>
                </Section>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Preview Panel (60%) */}
        <div className="flex-1 overflow-hidden bg-muted/30 p-6">
          <div className="flex h-full flex-col overflow-hidden">
            {/* Preview controls */}
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Live Preview</h2>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={autoScale}
                    onChange={(e) => setAutoScale(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Auto-fit
                </label>
                <PreviewScale />
              </div>
            </div>

            {/* Preview canvas */}
            <div
              ref={previewContainerRef}
              className="flex-1 overflow-hidden rounded-lg bg-background p-6"
            >
              <div className="flex h-full w-full items-center justify-center">
                <div
                  style={{
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease-in-out',
                  }}
                >
                  {config.formFactor === '4inch' ? (
                    <KioskMockup4inch config={config} />
                  ) : (
                    <KioskMockup5inch config={config} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crop Dialog */}
      <LogoCropDialog
        open={showCropDialog}
        imageUrl={getCurrentCropImageUrl()}
        initialCropArea={getCurrentCropArea()}
        onConfirm={handleCropConfirm}
        onCancel={closeCropDialog}
        title="Crop Media Logo"
      />
    </div>
  );
}
